import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Calendar, CreditCard, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { supabase } from '@/lib/supabase/client';
import { logger } from '@/lib/utils/logger';

interface Experience {
  id: string;
  title: string;
  price: number;
  status: string;
  bookings?: Booking[];
}

interface Booking {
  id: string;
  experience_id: string;
  participant_count: number;
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface CreatorData {
  id: string;
  business_name: string;
  contact_email: string | null;
  stripe_account_id: string | null;
  experiences: Experience[];
}

interface BusinessMetrics {
  business_name: string;
  contact_email: string | null;
  total_experiences: number;
  active_experiences: number;
  total_bookings: number;
  confirmed_bookings: number;
  pending_bookings: number;
  total_gmv: number;
  total_net_revenue: number;
  current_month_gmv: number;
  current_month_net_revenue: number;
  current_year_gmv: number;
  current_year_net_revenue: number;
  total_guests_served: number;
  transferred_funds: number;
  stripe_connected: boolean;
  last_booking_date: string | null;
}

interface DashboardData {
  metrics: BusinessMetrics;
  recent_bookings: any[];
  experiences: {
    experience_id: string;
    title: string;
    price: number;
    booking_count: number;
    revenue: number;
    net_revenue: number;
    participants: number;
    capacity_utilization: number;
    status: string;
  }[];
}

export function CreatorBusinessMetrics() {
  const { user, profile, session } = useUnifiedAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Ensure mountedRef is set to true on mount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (user?.id && (!hasFetchedRef.current || !dashboardData)) {
      hasFetchedRef.current = true;
      fetchDashboardData();
    }
    
    return () => {
      mountedRef.current = false;
      // Don't reset hasFetchedRef if we have data - this prevents re-fetching in StrictMode
      if (!dashboardData) {
        hasFetchedRef.current = false;
      }
    };
  }, [user, session]);

  const fetchDashboardData = async () => {
    try {
      // Set a timeout for mobile devices
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current && loading) {
          setError('Loading timeout - please refresh');
          setLoading(false);
        }
      }, 10000); // 10 second timeout
      
      if (!user?.id) {
        if (mountedRef.current) {
          setError('User not authenticated');
          setLoading(false);
        }
        return;
      }

      logger.info('Fetching creator dashboard data', { userId: user.id });

      // Ensure session before query
      if (!session) {
        throw new Error('No session available in auth store');
      }

      const { data: { session: currentSupabaseSession } } = await supabase.auth.getSession();
      if (!currentSupabaseSession) {
        const { error: setSessionError } = await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token
        });
        
        if (setSessionError) {
          logger.error('Failed to restore Supabase session', { error: setSessionError });
          throw setSessionError;
        }
        
        // Small delay to ensure session is fully set
        await new Promise(resolve => setTimeout(resolve, 25));
      }

      // Get creator profile first
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id, business_name, contact_email, stripe_account_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !creatorProfile) {
        throw new Error('Creator profile not found');
      }

      // Get experiences first
      const { data: experiences, error: expError } = await supabase
        .from('experiences')
        .select('id, title, price, status')
        .eq('creator_profile_id', creatorProfile.id);

      if (expError) throw expError;

      const experienceIds = experiences?.map(e => e.id) || [];
      
      // Ensure session is still valid before booking query
      const { data: { session: bookingSupabaseSession } } = await supabase.auth.getSession();
      if (!bookingSupabaseSession && session) {
        const { error: setSessionError } = await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token
        });
        
        if (setSessionError) {
          logger.error('Failed to restore Supabase session for booking query', { error: setSessionError });
          throw setSessionError;
        }
        
        await new Promise(resolve => setTimeout(resolve, 25));
      }
      
      // Get bookings for these experiences
      const { data: allBookings, error: bookingError } = experienceIds.length > 0 
        ? await supabase
            .from('bookings')
            .select(`
              id,
              experience_id,
              participant_count,
              total_amount,
              status,
              payment_status,
              created_at,
              user:users!bookings_user_id_fkey(id, full_name, email)
            `)
            .in('experience_id', experienceIds)
            .order('created_at', { ascending: false })
        : { data: [], error: null };

      if (bookingError) throw bookingError;

      // Create creator data object for consistency
      const creatorData: CreatorData = {
        id: creatorProfile.id,
        business_name: creatorProfile.business_name,
        contact_email: creatorProfile.contact_email,
        stripe_account_id: creatorProfile.stripe_account_id,
        experiences: experiences
      };
      
      // Filter paid and confirmed bookings
      const paidBookings = allBookings.filter(b => b.payment_status === 'paid');
      const confirmedBookings = paidBookings.filter(b => b.status === 'confirmed');
      const pendingBookings = allBookings.filter(b => b.status === 'pending');

      // Calculate metrics
      const totalGmv = confirmedBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const currentMonth = new Date();
      currentMonth.setDate(1);
      currentMonth.setHours(0, 0, 0, 0);
      
      const currentMonthBookings = confirmedBookings.filter(b => 
        new Date(b.created_at) >= currentMonth
      );
      const currentMonthGmv = currentMonthBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      
      const currentYear = new Date(new Date().getFullYear(), 0, 1);
      const currentYearBookings = confirmedBookings.filter(b => 
        new Date(b.created_at) >= currentYear
      );
      const currentYearGmv = currentYearBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      
      const totalGuestsServed = confirmedBookings.reduce((sum, b) => sum + (b.participant_count || 0), 0);
      
      // Calculate net revenue (after payment processing fees)
      // Using 4.5% estimate for payment fees
      const calculateNetRevenue = (bookings: any[]) => {
        return bookings.reduce((sum, booking) => {
          const grossAmount = booking.total_amount || 0;
          const stripeFee = grossAmount * 0.045; // 4.5% estimate
          const netAmount = grossAmount - stripeFee;
          return sum + netAmount;
        }, 0);
      };
      
      const totalNetRevenue = calculateNetRevenue(confirmedBookings);
      const currentMonthNetRevenue = calculateNetRevenue(currentMonthBookings);
      const currentYearNetRevenue = calculateNetRevenue(currentYearBookings);
      
      // Fetch actual transferred funds from transfers table
      let transferredFunds = 0;
      if (creatorData.stripe_account_id) {
        const { data: transfers, error: transfersError } = await supabase
          .from('transfers')
          .select('amount')
          .eq('destination', creatorData.stripe_account_id)
          .eq('status', 'paid');
        
        if (!transfersError && transfers) {
          // Convert from cents to euros
          transferredFunds = transfers.reduce((sum, transfer) => sum + (transfer.amount / 100), 0);
        }
      }
      
      const lastBookingDate = allBookings.length > 0 ? allBookings[0].created_at : null;

      // Build dashboard data
      const newDashboardData = {
        metrics: {
          business_name: creatorData.business_name,
          contact_email: creatorData.contact_email,
          total_experiences: experiences.length,
          active_experiences: experiences.filter(e => e.status === 'approved').length,
          total_bookings: allBookings.length,
          confirmed_bookings: confirmedBookings.length,
          pending_bookings: pendingBookings.length,
          total_gmv: totalGmv,
          total_net_revenue: totalNetRevenue,
          current_month_gmv: currentMonthGmv,
          current_month_net_revenue: currentMonthNetRevenue,
          current_year_gmv: currentYearGmv,
          current_year_net_revenue: currentYearNetRevenue,
          total_guests_served: totalGuestsServed,
          transferred_funds: transferredFunds,
          stripe_connected: !!creatorData.stripe_account_id,
          last_booking_date: lastBookingDate
        },
        recent_bookings: (allBookings || []).slice(0, 10) as any[],
        experiences: (experiences || []).map(exp => {
          const expBookings = confirmedBookings.filter(b => b.experience_id === exp.id);
          const revenue = expBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
          const netRevenue = calculateNetRevenue(expBookings);
          const participants = expBookings.reduce((sum, b) => sum + (b.participant_count || 0), 0);
          return {
            experience_id: exp.id,
            title: exp.title,
            price: exp.price,
            booking_count: expBookings.length,
            revenue: revenue,
            net_revenue: netRevenue,
            participants: participants,
            capacity_utilization: 0,
            status: exp.status
          };
        })
      };

      // Clear timeout if we got data
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Always try to update state, don't rely on mountedRef alone
      setDashboardData(newDashboardData);
      setLoading(false);
      
      logger.info('Dashboard data loaded successfully', {
        totalGmv: newDashboardData.metrics.total_gmv,
        bookingCount: newDashboardData.metrics.confirmed_bookings
      });
      
    } catch (err) {
      logger.error('Failed to fetch dashboard data', { error: err });
      
      // Clear timeout on error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateGrowthRate = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading business metrics...</p>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error || 'No data available'}</p>
        <Button variant="outline" size="sm" onClick={fetchDashboardData} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  const { metrics } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Business Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-sand-100">{metrics.business_name}</h1>
          <p className="text-sand-400">{metrics.contact_email}</p>
        </div>
        {!metrics.stripe_connected && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-sm text-amber-500">Setup Stripe Connect</span>
          </div>
        )}
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total GMV */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <span className="text-xs text-sand-400 uppercase tracking-wide">Total GMV</span>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-sand-100">{formatCurrency(metrics.total_gmv)}</p>
            <p className="text-sm text-sand-400">
              {formatCurrency(metrics.current_month_gmv)} this month
            </p>
          </div>
        </motion.div>

        {/* Net Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-purple-500" />
            </div>
            <span className="text-xs text-sand-400 uppercase tracking-wide">Net Revenue</span>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-sand-100">{formatCurrency(metrics.total_net_revenue)}</p>
            <p className="text-sm text-sand-400">
              After payment fees
            </p>
          </div>
        </motion.div>

        {/* Participants */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <span className="text-xs text-sand-400 uppercase tracking-wide">Participants</span>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-sand-100">{metrics.total_guests_served}</p>
            <p className="text-sm text-sand-400">
              Across {metrics.confirmed_bookings} bookings
            </p>
          </div>
        </motion.div>

        {/* Transferred Funds */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-earth-800/50 p-6 rounded-lg border border-earth-700/50"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-orange-500" />
            </div>
            <span className="text-xs text-sand-400 uppercase tracking-wide">Transferred</span>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-sand-100">{formatCurrency(metrics.transferred_funds)}</p>
            <p className="text-sm text-sand-400">
              {!metrics.stripe_connected && 'Connect Stripe'}
              {metrics.stripe_connected && metrics.transferred_funds === 0 && 'Pending'}
              {metrics.stripe_connected && metrics.transferred_funds > 0 && 'To your account'}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-earth-800/30 rounded-lg border border-earth-700/30">
        <Button 
          size="sm" 
          onClick={async () => {
            try {
              // Get JWT from Supabase auth
              const session = await supabase.auth.getSession();
              const jwt = session.data.session?.access_token;
              
              if (!jwt) {
                alert('Please sign in to export data');
                return;
              }
              
              // Call CSV export function
              const response = await fetch('/.netlify/functions/export-bookings-csv', {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${jwt}`,
                },
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Export failed: ${response.status} - ${errorText}`);
              }
              
              // Download CSV
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `bookings-export-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              
            } catch (error) {
              console.error('Export failed:', error);
              alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
          }}
        >
          Export Customer Data
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.location.href = '/creator/messaging'}>
          Message Guests
        </Button>
        {!metrics.stripe_connected && (
          <Button size="sm" variant="primary" onClick={() => window.location.href = '/creator?tab=payments'}>
            Setup Stripe Connect
            <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
        )}
      </div>

      {/* Last Activity */}
      {metrics.last_booking_date && (
        <p className="text-sm text-sand-400">
          Last booking: {new Date(metrics.last_booking_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      )}
    </div>
  );
} 